/**
 * AppStorageLocalstorage bond setup
 *
 * Wires @molecule/app-storage-localstorage to @molecule/app-storage.
 *
 * The composition root also declares which keys the app's PREVIOUS localStorage
 * helper wrote verbatim, so those values survive the move to the bonded
 * provider instead of being reset by its JSON codec:
 *
 * - `mol_initial_prompt_<projectId>` — the hand-off prompt typed on the landing
 *   page / dashboard (free-form text).
 * - `mol-chat-conv:<projectId>` — the last selected conversation id.
 * - `molecule_last_project` — the project id `/workspace` redirects back to.
 *
 * Everything else (booleans, numbers, JSON objects) keeps the JSON codec, so a
 * typed preference is unchanged. Declaring a string key here is the ONLY place
 * this compatibility lives — no consumer reads the browser global, and no
 * stored value is deleted to make the switch.
 */

import { createStringCodec, setProvider } from '@molecule/app-storage'
import { createLocalStorageProvider } from '@molecule/app-storage-localstorage'

/** Exact keys the previous helper stored as raw strings. */
const LEGACY_RAW_STRING_KEYS = new Set(['molecule_last_project'])

/** Key prefixes the previous helper stored as raw strings. */
const LEGACY_RAW_STRING_PREFIXES = ['mol_initial_prompt_', 'mol-chat-conv:'] as const

/**
 * Whether a key holds a value written verbatim by the previous storage helper.
 *
 * Judged on the UNPREFIXED key, the same string consumers pass to
 * `@molecule/app-storage`.
 * @param key - The storage key.
 * @returns `true` when the value is a plain string, not JSON.
 */
export function isLegacyRawStringKey(key: string): boolean {
  if (LEGACY_RAW_STRING_KEYS.has(key)) return true
  return LEGACY_RAW_STRING_PREFIXES.some((prefix) => key.startsWith(prefix))
}

export function setupAppStorageLocalstorage(): void {
  setProvider(
    createLocalStorageProvider({
      codecs: [createStringCodec(isLegacyRawStringKey)],
    }),
  )
}
